// Firmark Calcs — pure derivation + formatting shared by the calc sheet and the
// print view. Single source of truth for DCR color, number formatting, and the
// fused demand/capacity-plus-NDS-clause ledger rows.

import type { CalcCheck, CalcSummaryRow } from "../../types/calc";

// --- formatting (moved here from CalcReportView; single source) --------------

// D/C status ink — the design system's status QUADS (firmark-calc-tokens.css),
// never the raw shell --color-status-* hexes. One palette for every calc
// surface (viewer, picker, sheet, diagrams); dark mode re-tunes the quads.
export function utilColor(dcr: number | null): string {
  if (dcr == null) return "var(--color-text-muted)";
  if (dcr > 1.0) return "var(--calc-fail-fg)";
  if (dcr >= 0.9) return "var(--calc-near-fg)";
  return "var(--calc-pass-fg)";
}

// D/C canon: percent form. Integer precision for tiles / chips / badges /
// banners ("62%"), one decimal (pct1, "62.0%") only in DCR table columns where
// near-identical combinations must stay distinguishable.
export function pct(dcr: number | null): string {
  if (dcr == null) return "—";
  return `${Math.round(dcr * 100)}%`;
}

export function pct1(dcr: number | null): string {
  if (dcr == null) return "—";
  return `${(dcr * 100).toFixed(1)}%`;
}

export function prettyLabel(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Unit typesetting at RENDER only (stored/engine/registry unit strings stay
// raw): mid-dot compound units (lb·ft, kip·ft, lb·in²) and superscript section
// units (in², in³, in⁴, ft²).
export function prettyUnits(units: string): string {
  return units
    .replace(/\b(lb|kip)-(ft|in)\b/g, "$1·$2")
    .replace(/\b(in|ft)2\b/g, "$1²")
    .replace(/\bin3\b/g, "in³")
    .replace(/\bin4\b/g, "in⁴");
}

// Dimension strings ("2x10", "3.5x11.875") typeset with a real multiplication
// sign at render; the stored callout stays raw ASCII.
export function prettySize(size: string): string {
  return size.replace(/(\d)\s*[x]\s*(?=[\d.])/gi, "$1×");
}

// Compact: whole when |v|>=100, else 2dp, with thousands separators.
export function fmtVal(v: number | null): string {
  if (v == null) return "—";
  const n = Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 100) / 100;
  return n.toLocaleString();
}

// --- fused ledger row (summary natural-units + checks dcr/ndsRef) ------------

export type FusedRow = {
  key: string;
  label: string;
  demandSymbol: string;
  capacitySymbol: string;
  demand: number | null;
  capacity: number | null;
  units: string;
  dcr: number | null;
  governingCombo: string | null;
  ndsRef: string | null;
  passes: boolean;
};

// Fail-safe outer join of the natural-unit summary rows with the per-limit-state
// checks (which carry dcr + the NDS clause). Keyed summary.key <-> check.limitState.
// A row present in only one array still renders (missing cells blank) — never
// drop a limit state.
export function fuseChecks(
  summary: CalcSummaryRow[] | undefined,
  checks: CalcCheck[] | undefined,
): FusedRow[] {
  const sum = summary ?? [];
  const chk = checks ?? [];
  const byState = new Map(chk.map((c) => [c.limitState, c]));
  const used = new Set<string>();

  const rows: FusedRow[] = sum.map((s) => {
    const c = byState.get(s.key);
    if (c) used.add(s.key);
    return {
      key: s.key,
      label: s.label,
      demandSymbol: s.demandSymbol,
      capacitySymbol: s.capacitySymbol,
      demand: s.demand,
      capacity: s.capacity,
      units: s.units,
      dcr: s.utilization ?? c?.dcr ?? null,
      governingCombo: s.governingCombo ?? c?.governingCombo ?? null,
      ndsRef: c?.ndsRef ?? null,
      passes: s.passes,
    };
  });

  for (const c of chk) {
    if (used.has(c.limitState)) continue;
    rows.push({
      key: c.limitState,
      label: prettyLabel(c.limitState),
      demandSymbol: "",
      capacitySymbol: "",
      demand: c.demand,
      capacity: c.capacity,
      units: c.units ?? "",
      dcr: c.dcr,
      governingCombo: c.governingCombo,
      ndsRef: c.ndsRef,
      passes: c.passes,
    });
  }

  return rows;
}

// The NDS clause for the governing limit state, for the verdict band.
export function governingClause(
  governing: string | null,
  checks: CalcCheck[] | undefined,
): string | null {
  if (!governing) return null;
  const c = (checks ?? []).find((x) => x.limitState === governing);
  return c?.ndsRef ?? null;
}
